import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as React from 'react'
import { FixedSizeList as List } from 'react-window'

import { KEYBOARD } from '../../helpers/constants'
import { HandlerOf } from '../../helpers/typeHelper'
import Overlay from '../Overlay/Overlay'
import { DropdownOption, DropdownProps } from './Dropdown'
import { DropdownOpenerContainer, EmptyOption, Option, Options } from './Dropdown.styles'

declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null
}

export interface VirtualDropDownOpenerProps<T>
  extends Omit<DropdownProps<T>, 'children' | 'optionsLabel' | 'maxHeight' | 'width'> {
  children: (isOpen: boolean, setOpen: HandlerOf<boolean>) => React.ReactNode
  maxHeight: number
  width: number | string
  itemSize?: number
}

export const VirtualDropDownOpener = forwardRef(function VirtualDropDownOpener<T>(
  {
    options,
    isInitiallyOpen = false,
    closeOnClick = true,
    children,
    bottom,
    align,
    itemSize = 40,
    width,
    maxHeight,
    emptyOptionsLabel,
    onChange,
    onBlur,
    onNonFormBlur,
    onToggle,
    className,
  }: VirtualDropDownOpenerProps<T>,
  ref: any
) {
  const [isOpen, setOpen] = useState(isInitiallyOpen)
  const wrap = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    openDropdown: () => setOpen(true),
    closeDropdown: () => setOpen(false),
    focus: () => wrap.current?.focus(),
  }))

  const handleToggleOptions = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggle?.(!isOpen)
    setOpen((prevState) => !prevState)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onBlur?.()
    onNonFormBlur?.()
    setOpen(false)
  }

  const handleOnClick = (option: DropdownOption<T>) => {
    closeOnClick && setOpen(false)
    option.value && onChange?.(option.value)
    option.onClick && option.onClick()
  }

  const handleKeyDown = useCallback((event) => {
    if (event.key === KEYBOARD.ESCAPE) setOpen(false)
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  const Row = ({ index, style }: any) => {
    const option = options[index]
    return (
      <div style={style}>
        {option.renderer ? (
          option.renderer(option, () => setOpen(false))
        ) : (
          <Option
            key={String(option.value)}
            onClick={() => handleOnClick(option)}
            data-select-option={option.label}
          >
            {option.dropDownLabel || option.label}
          </Option>
        )}
      </div>
    )
  }

  function calculateHeight() {
    if (options.length * itemSize > maxHeight) return maxHeight
    // +2 is for the border, otherwise a scrollbar will always appear
    return Math.max(options.length * itemSize + 2, itemSize)
  }

  return (
    <DropdownOpenerContainer ref={wrap}>
      <div onClick={handleToggleOptions} className={className}>
        {children(isOpen, setOpen)}
      </div>
      {isOpen && (emptyOptionsLabel || Boolean(options.length)) && (
        <>
          <Overlay onClick={handleOverlayClick} isTransparent={true} />
          <Options $bottom={bottom} $width={width} $align={align} $maxHeight={maxHeight} $overflow="visible">
            {options.length ? (
              <List height={calculateHeight()} itemCount={options.length} itemSize={itemSize} width={width}>
                {Row}
              </List>
            ) : (
              <EmptyOption>{emptyOptionsLabel}</EmptyOption>
            )}
          </Options>
        </>
      )}
    </DropdownOpenerContainer>
  )
})
